/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { 
  BookOpen, 
  Calendar, 
  Clock, 
  MapPin, 
  User,
  Filter,
  Loader2,
  Info,
  GraduationCap
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { getCurrentAcademicPeriod } from '../../utils/academicYear';

interface SubjectsOfferingProps {
  studentId: string;
  yearLevel?: number;
  course?: string;
}

export function SubjectsOffering({ studentId, yearLevel: propYearLevel, course: propCourse }: SubjectsOfferingProps) {
  const { user, accessToken } = useAuth();
  const { academicYear: defaultAcademicYear, semesterLabel: defaultSemester } = getCurrentAcademicPeriod();
  const [academicYear, setAcademicYear] = useState<string>(defaultAcademicYear);
  const [semester, setSemester] = useState<string>(defaultSemester);
  const [availableSubjects, setAvailableSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Derive student's year level and course from props or auth context
  const studentYearLevel = propYearLevel || user?.yearLevel || undefined;
  const studentCourse = propCourse || user?.course || undefined;
  const studentCurriculumVersion = user?.curriculumVersion || undefined;

  // Fetch subjects when academic year, semester, or year level changes
  useEffect(() => {
    fetchSubjects();
  }, [academicYear, semester, studentYearLevel, accessToken]);

  const fetchSubjects = async () => {
    if (!accessToken) return;
    
    setLoading(true);
    try {
      console.log('Fetching subjects with filters:', { academicYear, semester, yearLevel: studentYearLevel });
      const response = await api.getStudentSubjects(academicYear, semester, accessToken, studentYearLevel);
      setAvailableSubjects(response.subjects);
      console.log('Fetched subjects:', response.subjects.length, response.subjects);
      
      if (response.subjects.length === 0) {
        console.warn('No subjects found. Check:', {
          academicYear,
          semester,
          yearLevel: studentYearLevel,
          expectedSemesterNumber: semester === '1st Semester' ? 1 : semester === '2nd Semester' ? 2 : 3
        });
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      toast.error('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  const getDayName = (dayOfWeek: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek] || 'Unknown';
  };

  const getYearLevelLabel = (level: number) => {
    if (level === 0) {
      return 'All Year Levels';
    }

    const labels: Record<number, string> = { 1: '1st Year', 2: '2nd Year', 3: '3rd Year' };
    return labels[level] || `Year ${level}`;
  };

  const totalUnits = availableSubjects.reduce((sum, subject) => sum + (subject?.units || 0), 0);

  // Generate academic year options (current year and next 2 years)
  const currentYear = new Date().getFullYear();
  const academicYearOptions = [
    `${currentYear - 1}-${currentYear}`,
    `${currentYear}-${currentYear + 1}`,
    `${currentYear + 1}-${currentYear + 2}`,
    `${currentYear + 2}-${currentYear + 3}`,
  ];

  const semesterOptions = ['1st Semester', '2nd Semester', 'Summer'];

  // Helper to format time in 12-hour format with AM/PM
  function formatTime12Hour(time24: string) {
    if (!time24) return '';
    const [hourStr, minuteStr] = time24.split(":");
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr || "00";
    if (isNaN(hour)) return time24;
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minute.padStart(2, "0")} ${ampm}`;
  }

  return (
    <div className="space-y-6">
      {/* Student Program & Year Level Info Card */}
      <Alert className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <GraduationCap className="h-4 w-4 text-blue-600" />
        <AlertDescription>
          <div className="space-y-2">
            <p className="font-medium text-blue-900">Subjects offered for your program and year level</p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-blue-700">Program:</span>
                <Badge variant="secondary" className="font-mono font-semibold bg-blue-100 text-blue-800">
                  {studentCourse || 'Not assigned'}
                </Badge>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-blue-700">Year Level:</span>
                <Badge variant="secondary" className="font-mono font-semibold bg-indigo-100 text-indigo-800">
                  {studentYearLevel ? getYearLevelLabel(studentYearLevel) : 'Not assigned'}
                </Badge>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-blue-700">Curriculum:</span>
                <Badge variant="secondary" className="font-mono font-semibold bg-sky-100 text-sky-800">
                  {studentCurriculumVersion || 'Not assigned'}
                </Badge>
              </div>
            </div>
            <p className="text-xs text-blue-600/80 mt-1">
              Showing subjects specifically scheduled for your program, year level, and assigned curriculum in the selected semester.
            </p>
          </div>
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Subjects Offered
            {studentYearLevel && (
              <Badge variant="outline" className="ml-1 text-xs">
                {getYearLevelLabel(studentYearLevel)}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {studentCourse && studentYearLevel
              ? `Subjects for ${studentCourse} — ${getYearLevelLabel(studentYearLevel)}, ${semester}, S.Y. ${academicYear}`
              : `View subjects scheduled for ${semester}, School Year ${academicYear}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filter Section */}
          <div className="mb-6 p-4 border rounded-lg bg-muted/30">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filter by Academic Year and Semester
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="academic-year">Academic Year</Label>
                <Select value={academicYear} onValueChange={setAcademicYear}>
                  <SelectTrigger id="academic-year">
                    <SelectValue placeholder="Select academic year" />
                  </SelectTrigger>
                  <SelectContent>
                    {academicYearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="semester">Semester</Label>
                <Select value={semester} onValueChange={setSemester}>
                  <SelectTrigger id="semester">
                    <SelectValue placeholder="Select semester" />
                  </SelectTrigger>
                  <SelectContent>
                    {semesterOptions.map((sem) => (
                      <SelectItem key={sem} value={sem}>
                        {sem}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
              <p className="text-sm text-muted-foreground">
                Showing subjects for{' '}
                {studentCourse && <span className="font-medium">{studentCourse}</span>}
                {studentYearLevel && <span className="font-medium"> — {getYearLevelLabel(studentYearLevel)}</span>}
                {' '}in <span className="font-medium">{semester}</span> of S.Y. <span className="font-medium">{academicYear}</span>
              </p>
              {availableSubjects.length > 0 && (
                <Badge variant="outline" className="w-fit">
                  {availableSubjects.length} subject{availableSubjects.length !== 1 ? 's' : ''} • {totalUnits} total units
                </Badge>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading subjects...</span>
            </div>
          ) : availableSubjects.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No subjects available for {semester} of Academic Year {academicYear}
              </p>
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md mx-auto text-left text-sm">
                <p className="font-medium text-blue-900 mb-2">Information:</p>
                <ul className="space-y-1 text-blue-800">
                  <li>• Subjects are filtered by your program: <strong>{studentCourse || 'Not assigned'}</strong></li>
                  <li>• Subjects are filtered by your year level: <strong>{studentYearLevel ? getYearLevelLabel(studentYearLevel) : 'Not assigned'}</strong></li>
                  <li>• Subjects use your curriculum assignment: <strong>{studentCurriculumVersion || 'Not assigned'}</strong></li>
                  <li>• Make sure schedules exist for the selected semester and academic year</li>
                  <li>• Contact your administrator if you believe subjects should be available</li>
                </ul>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Try selecting a different academic year or semester
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {availableSubjects.map((subject) => (
                <Card key={subject.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex flex-col space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{subject.code}</h3>
                            <Badge variant="outline">{subject.units} unit{subject.units !== 1 ? 's' : ''}</Badge>
                            {subject.curriculumVersion && (
                              <Badge variant="secondary" className="text-xs">
                                Curriculum {subject.curriculumVersion}
                              </Badge>
                            )}
                            {subject.yearLevel !== undefined && subject.yearLevel !== null && (
                              <Badge variant="secondary" className="text-xs">
                                {getYearLevelLabel(subject.yearLevel)}
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-medium text-base mb-1">{subject.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {subject.description}
                          </p>
                        </div>
                      </div>
                      
                      {subject.schedule && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t text-sm">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">Day:</span>
                              <span>{getDayName(subject.schedule.dayOfWeek)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">Time:</span>
                              <span>{formatTime12Hour(subject.schedule.startTime)} - {formatTime12Hour(subject.schedule.endTime)}</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">Room:</span>
                              <span>{subject.room?.name || 'TBA'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">Faculty:</span>
                              <span>{subject.faculty?.name || 'TBA'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}